import { useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { Provider, useDispatch, useSelector } from 'react-redux'
import { ThemeProvider } from '@mui/material/styles'
import { CssBaseline } from '@mui/material'
import { theme } from './styles/theme'
import { store, RootState } from './store'
import { QueryProvider } from './providers/QueryProvider'
import { ToastProvider } from './contexts/ToastContext'
import Layout from './components/layout/Layout'
import LoginPage from './pages/auth/LoginPage'
import UnauthorizedPage from './pages/UnauthorizedPage'
import Dashboard from './pages/dashboard/Dashboard'
import BillListPage from './pages/bills/BillListPage'
import BillDetailsPage from './pages/bills/BillDetailsPage'
import BillEditPage from './pages/bills/BillEditPage'
import LoadFormsPage from './pages/loadforms/LoadFormsPage'
import LoadFormDetailsPage from './pages/loadforms/LoadFormDetailsPage'
import LoadFormSettlementPage from './pages/loadforms/LoadFormSettlementPage'
import CustomerListPage from './pages/customers/CustomerListPage'
import CustomerDetailsPage from './pages/customers/CustomerDetailsPage'
import CustomerLedgerPage from './pages/customers/CustomerLedgerPage'
import SKUListPage from './pages/skus/SKUListPage'
import SKUDetailsPage from './pages/skus/SKUDetailsPage'
import AreaListPage from './pages/areas/AreaListPage'
import UserListPage from './pages/users/UserListPage'
import TargetsPage from './pages/targets/TargetsPage'
import ReportsPage from './pages/reports/ReportsPage'
import SalesTaxReportPage from './pages/reports/SalesTaxReportPage'
import PnlReportPage from './pages/reports/PnlReportPage'
import ArAgingReportPage from './pages/reports/ArAgingReportPage'
import BalanceSheetReportPage from './pages/reports/BalanceSheetReportPage'
import PurchaseInvoiceListPage from './pages/purchase-invoices/PurchaseInvoiceListPage'
import PurchaseInvoiceNewPage from './pages/purchase-invoices/PurchaseInvoiceNewPage'
import PurchaseInvoiceDetailPage from './pages/purchase-invoices/PurchaseInvoiceDetailPage'
import InventoryPage from './pages/inventory/InventoryPage'
import VendorsPage from './pages/settings/VendorsPage'
import AccountLedgerPage from './pages/account-ledger/AccountLedgerPage'
import ExpensesPage from './pages/expenses/ExpensesPage'
import ProtectedRoute from './components/auth/ProtectedRoute'
import RoleRoute from './components/auth/RoleRoute'
import CompanyListPage from './pages/companies/CompanyListPage'
import CompanyCreatePage from './pages/companies/CompanyCreatePage'
import CompanyDetailPage from './pages/companies/CompanyDetailPage'
import { fetchCurrentUser } from './store/slices/authSlice'

function RoleHome() {
  const role = useSelector((state: RootState) => state.auth.user?.role)
  return <Navigate to={role === 'dev' ? '/companies' : '/dashboard'} replace />
}

function AppContent() {
  const dispatch = useDispatch()
  const { token, user } = useSelector((state: RootState) => state.auth)

  useEffect(() => {
    // If there's a token but no user, fetch the current user
    if (token && !user) {
      dispatch(fetchCurrentUser() as any)
    }
  }, [token, user, dispatch])

  useEffect(() => {
    document.documentElement.style.setProperty('--tenant-color', user?.company_branding?.header_color || theme.palette.primary.main)
  }, [user?.company_branding?.header_color])

  return (
    <Router>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/unauthorized" element={<UnauthorizedPage />} />
        <Route 
          path="/*" 
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<RoleHome />} />
          <Route path="companies" element={<RoleRoute role="dev"><CompanyListPage /></RoleRoute>} />
          <Route path="companies/new" element={<RoleRoute role="dev"><CompanyCreatePage /></RoleRoute>} />
          <Route path="companies/:id" element={<RoleRoute role="dev"><CompanyDetailPage /></RoleRoute>} />
          <Route path="dashboard" element={<RoleRoute role="tenant"><Dashboard /></RoleRoute>} />
          <Route path="bills" element={<RoleRoute role="tenant"><BillListPage /></RoleRoute>} />
          <Route path="bills/:id" element={<RoleRoute role="tenant"><BillDetailsPage /></RoleRoute>} />
          <Route path="bills/:id/edit" element={<RoleRoute role="tenant"><BillEditPage /></RoleRoute>} />
          <Route path="customers" element={<RoleRoute role="tenant"><CustomerListPage /></RoleRoute>} />
          <Route path="customers/:id" element={<RoleRoute role="tenant"><CustomerDetailsPage /></RoleRoute>} />
          <Route path="customers/:id/ledger" element={<RoleRoute role="tenant"><CustomerLedgerPage /></RoleRoute>} />
          <Route path="skus" element={<RoleRoute role="tenant"><SKUListPage /></RoleRoute>} />
          <Route path="skus/:id" element={<RoleRoute role="tenant"><SKUDetailsPage /></RoleRoute>} />
          <Route path="areas" element={<RoleRoute role="tenant"><AreaListPage /></RoleRoute>} />
          <Route path="users" element={<RoleRoute role="tenant"><UserListPage /></RoleRoute>} />
          <Route path="targets" element={<RoleRoute role="tenant"><TargetsPage /></RoleRoute>} />
          <Route path="reports" element={<RoleRoute role="tenant"><ReportsPage /></RoleRoute>} />
          <Route path="reports/sales-tax" element={<RoleRoute role="tenant"><SalesTaxReportPage /></RoleRoute>} />
          <Route path="reports/pnl" element={<RoleRoute role="tenant"><PnlReportPage /></RoleRoute>} />
          <Route path="reports/ar-aging" element={<RoleRoute role="tenant"><ArAgingReportPage /></RoleRoute>} />
          <Route path="reports/balance-sheet" element={<RoleRoute role="tenant"><BalanceSheetReportPage /></RoleRoute>} />
          <Route path="loadforms" element={<RoleRoute role="tenant"><LoadFormsPage /></RoleRoute>} />
          <Route path="loadforms/:id" element={<RoleRoute role="tenant"><LoadFormDetailsPage /></RoleRoute>} />
          <Route path="loadforms/:id/settlement" element={<RoleRoute role="tenant"><LoadFormSettlementPage /></RoleRoute>} />
          <Route path="purchase-invoices" element={<RoleRoute role="tenant"><PurchaseInvoiceListPage /></RoleRoute>} />
          <Route path="purchase-invoices/new" element={<RoleRoute role="tenant"><PurchaseInvoiceNewPage /></RoleRoute>} />
          <Route path="purchase-invoices/:id" element={<RoleRoute role="tenant"><PurchaseInvoiceDetailPage /></RoleRoute>} />
          <Route path="inventory" element={<RoleRoute role="tenant"><InventoryPage /></RoleRoute>} />
          <Route path="settings/vendors" element={<RoleRoute role="tenant"><VendorsPage /></RoleRoute>} />
          <Route path="account-ledger" element={<RoleRoute role="tenant"><AccountLedgerPage /></RoleRoute>} />
          <Route path="expenses" element={<RoleRoute role="tenant"><ExpensesPage /></RoleRoute>} />
          <Route path="*" element={<RoleHome />} />
        </Route>
      </Routes>
    </Router>
  )
}

function App() {
  return (
    <Provider store={store}>
      <QueryProvider>
        <ToastProvider>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <AppContent />
          </ThemeProvider>
        </ToastProvider>
      </QueryProvider>
    </Provider>
  )
}

export default App
